import React, { useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// import { Button } from '@arco-design/web-react';

import ChatLayout from '../ChatLayout';
import Conversation from './Conversation';
import AvatarContainer from '@/components/AvatarContainer';
import SettingsPanel from '@/components/SettingsPanel';
import { updateUserTalking, setAutoExitTimeout } from '@/store/slices/room';
import { useAppSettings } from '@/lib/hooks/useAppSettings';
import { useScene, useDeviceState } from '@/lib/useCommon';
import RtcClient from '@/lib/RtcClient';

// 导入语音交互相关Hooks
import { useAudioSystem } from './hooks/useAudioSystem';
import { useVoiceInteraction } from './hooks/useVoiceInteraction';
import { useKeyboardControls } from './hooks/useKeyboardControls';
import { useAutoQuestion } from './hooks/useAutoQuestion';
import { useUserTalkingDetection } from './hooks/useUserTalkingDetection';
// import AudioController from './components/AudioController';
// import InteractionController from './components/InteractionController';
import { useLayout } from '@/lib/useLayout';

import styles from './index.module.less';

import logger from '@/utils/logger';

// 新增：统一的底部控件组件
import BottomControls from '../BottomControls';

/**
 * Room组件 - 主要聊天界面
 * 使用模块化Hooks实现功能分离和性能优化
 */
function Room() {
  const dispatch = useDispatch();
  

  
  // 优化selector使用，减少不必要的重渲染
  const { msgHistory, isAITalking, isUserTalking, localUser, scene } = useSelector(
    (state: any) => ({
      msgHistory: state.room.msgHistory,
      isAITalking: state.room.isAITalking,
      isUserTalking: state.room.isUserTalking,
      localUser: state.room.localUser,
      scene: state.room.scene,
    }),
    // 浅比较优化
    (left, right) => 
      left.msgHistory === right.msgHistory &&
      left.isAITalking === right.isAITalking &&
      left.isUserTalking === right.isUserTalking &&
      left.localUser === right.localUser &&
      left.scene === right.scene
  );
  
  // 使用统一的设置管理
  const {
    settingsConfig,
    pushToTalkMode,
    effectiveInterruptMode,
    autoExitTimeout,
  } = useAppSettings();
  
  // 缓存URL参数解析
  const questionFromURL = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('Question');
  }, []);
  
  const { isAudioPublished } = useDeviceState();
  const { botName } = useScene();

  // 调试URL参数
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const questionParam = urlParams.get('Question');
    const directRoomParam = urlParams.get('DirectRoom');
    logger.debug('[Room] URL参数调试:', {
      questionParam,
      directRoomParam,
      questionFromURL,
      fullURL: window.location.href,
      search: window.location.search,
    });
  }, [questionFromURL]);

  // 初始化超时退出设置
  useEffect(() => {
    const savedTimeout = sessionStorage.getItem('autoExitTimeout');
    if (savedTimeout) {
      const timeoutValue = parseInt(savedTimeout, 10);
      if (!isNaN(timeoutValue)) {
        dispatch(setAutoExitTimeout(timeoutValue));
        logger.info(`[Room] 从sessionStorage恢复超时设置: ${timeoutValue}ms`);
      }
    }
  }, [dispatch]);

  // 确保 Room 页面也能加载场景的主题配置（包括数字人视频）
  useEffect(() => {
    if (!scene) return;

    // 优先使用按场景命名空间缓存；若缺失则尝试加载
    const nsVideos = sessionStorage.getItem(`theme:${scene}:videos`);
    const nsHero = sessionStorage.getItem(`theme:${scene}:hero`);

    // 清理旧版全局键，避免跨场景污染
    try {
      sessionStorage.removeItem('themeAvatarVideos');
      sessionStorage.removeItem('themeBackgroundImage');
      sessionStorage.removeItem('themeHeroCSS');
      sessionStorage.removeItem('themeConfig');
    } catch {}

    // 若已有命名空间背景样式，立即应用
    if (nsHero) {
      // 由 SceneThemeProvider 统一应用 --theme-hero
    }

    if (nsVideos) {
      logger.debug('[Room] 已存在按场景缓存的主题视频配置');
      return;
    }

    // 加载场景配置
    const loadSceneTheme = async () => {
      try {
        logger.debug(`[Room] 加载场景主题配置: ${scene}`);
        const { default: Apis } = await import('@/app/index');
        const data = await Apis.Basic.getSceneSuggestions({ scene });
        
        // 处理数字人视频映射
        const videos: any =
          (!Array.isArray(data) && data?.theme?.videos) ||
          (!Array.isArray(data) && data?.avatarVideos) ||
          (!Array.isArray(data) && data?.videos);
        
        if (videos && typeof videos === 'object') {
          const payload: Partial<
            Record<'greet' | 'default' | 'talk' | 'think' | 'listen', string>
          > = {};
          ['greet', 'default', 'talk', 'think', 'listen'].forEach((k) => {
            if (typeof videos[k] === 'string' && videos[k]) {
              // 处理相对路径，添加前缀斜杠
              const videoPath =
                videos[k].startsWith('http') || videos[k].startsWith('/')
                  ? videos[k]
                  : `/${videos[k]}`;
              (payload as any)[k] = videoPath;
            }
          });
          
          if (Object.keys(payload).length > 0) {
            sessionStorage.setItem(`theme:${scene}:videos`, JSON.stringify(payload));
            logger.info('[Room] 主题视频映射已注入:', payload);
          }
        }

        // 处理背景图片
        const backgroundImage = (!Array.isArray(data) && data?.backgroundImage);
        if (backgroundImage && typeof backgroundImage === 'string') {
          const backgroundUrl = backgroundImage.startsWith('http') 
            ? backgroundImage 
            : `/${backgroundImage}`;
          const themeHero = `url("${backgroundUrl}") center center / cover no-repeat, radial-gradient(ellipse at 20% 10%, #2ec7a7 0%, #1e3c72 60%, #2a5298 100%), linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)`;
          
          // 缓存背景图片配置到sessionStorage（按场景命名空间）
          sessionStorage.setItem(`theme:${scene}:bg`, backgroundUrl);
          sessionStorage.setItem(`theme:${scene}:hero`, themeHero);
          
          // 由 SceneThemeProvider 统一应用 --theme-hero
          logger.info('[Room] 背景图片已注入:', backgroundUrl);
        }

        // 处理主题配置整合（包括校徽）
        const themeConfigData: any = {
          sceneProcessed: true, // 标记已处理场景配置
        };
        
        // 校徽图片
        const logoImage = (!Array.isArray(data) && data?.logoImage) || 
                         (!Array.isArray(data) && data?.theme?.logoImage);
        if (logoImage && typeof logoImage === 'string') {
          themeConfigData.logoImage = logoImage;
        }

        // 保存主题配置到 sessionStorage（按场景命名空间，始终保存，即使只有标记）
        sessionStorage.setItem(`theme:${scene}:config`, JSON.stringify(themeConfigData));
        // 通知同页 Hook 刷新（同页写入不会触发 storage 事件）
        window.dispatchEvent(new CustomEvent('theme-config-updated'));
        logger.info('[Room] 主题配置已注入:', themeConfigData);
      } catch (error) {
        logger.warn('[Room] 加载场景主题配置失败:', error);
      }
    };

    loadSceneTheme();
  }, [scene]);

  // 资源清理统一简化为各自hook的 useEffect cleanup

  // ==================== 音频系统管理 ====================
  const audioSystemConfig = useMemo(
    () => ({
      volumeUpdateInterval: 100,
      smoothingTimeConstant: 0.8,
      fftSize: 2048,
    }),
    []
  );

  const audioSystem = useAudioSystem(
    // 音量变化回调
    useCallback((volume: number) => {
      // 这里可以添加额外的音量处理逻辑，但不要在这里打印日志
      // 因为这会在每次音量变化时都触发
    }, []),
    audioSystemConfig
  );

  // 启动音频监控（只在首次满足可用条件时启动一次）
  const hasStartedAudioRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasStartedAudioRef.current && audioSystem.isSupported) {
      hasStartedAudioRef.current = true;
      logger.info('[Room] 启动音频监控');
      audioSystem.startVolumeMonitoring().catch((error) => {
        logger.error('[Room] 启动音频监控失败:', error);
      });
    }
  }, [audioSystem, audioSystem.isSupported]);

  // 组件卸载时确保停止音频监控（避免依赖 audioSystem 对象频繁变化导致重复清理）
  const stopAudioMonitoringRef = React.useRef(audioSystem.stopVolumeMonitoring);
  React.useEffect(() => {
    stopAudioMonitoringRef.current = audioSystem.stopVolumeMonitoring;
  }, [audioSystem.stopVolumeMonitoring]);
  React.useEffect(() => {
    return () => {
      stopAudioMonitoringRef.current?.();
    };
  }, []);

  // ==================== 语音交互管理 ====================
  const voiceInteraction = useVoiceInteraction({
    interruptTimeout: 500,
    waitAIStopTimeout: 500,
    delayAfterStop: 200,
  });

  // ==================== 键盘控制管理 ====================
  const [isHoldingTalkButton, setIsHoldingTalkButton] = React.useState(false);



  useKeyboardControls(
    {
      talkKey: ' ', // 空格键
      interruptKey: 'Escape',
      enablePreventDefault: true,
    },
    {
      onTalkKeyDown: useCallback(() => {
        if (pushToTalkMode) {
          setIsHoldingTalkButton(true);
          logger.debug('[Room] 按住说话开始');
        }
      }, [pushToTalkMode]),
      onTalkKeyUp: useCallback(() => {
        if (pushToTalkMode && isHoldingTalkButton) {
          setIsHoldingTalkButton(false);
          logger.debug('[Room] 按住说话结束');
        }
      }, [pushToTalkMode, isHoldingTalkButton]),
      onInterruptKey: useCallback(() => {
        if (isAITalking) {
          voiceInteraction.handleManualInterrupt();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isAITalking, voiceInteraction.handleManualInterrupt]),
    }
  );

  // ==================== 自动提问管理 ====================
  const { triggerAutoQuestion, hasSent } = useAutoQuestion(
    questionFromURL,
    {
      clearUrlAfterSend: true,
    },
    {
      onSendQuestion: voiceInteraction.sendMessage,
      onQuestionSent: useCallback((question: string) => {
        logger.info('[Room] 自动提问已发送:', question);
      }, []),
      onError: useCallback((error: Error, question: string) => {
        logger.error('[Room] 自动提问发送失败:', error, question);
      }, []),
    }
  );

  // ==================== 核心判断逻辑：欢迎语结束时自动发送提问（增强版） ====================
  // 统一一次性调度与去抖、并增加兜底定时触发，避免错过窗口
  const autoQuestionScheduledRef = React.useRef(false);
  const autoQuestionTimerRef = React.useRef<number | null>(null);
  const aiIdleDebounceTimerRef = React.useRef<number | null>(null);
  const fallbackAutoQuestionTimerRef = React.useRef<number | null>(null);

  const scheduleAutoQuestionOnce = React.useCallback(
    (source: string, delay = 350) => {
      if (!questionFromURL || hasSent || autoQuestionScheduledRef.current) return;

      autoQuestionScheduledRef.current = true;
      if (autoQuestionTimerRef.current) {
        clearTimeout(autoQuestionTimerRef.current);
        autoQuestionTimerRef.current = null;
      }

      logger.info('[Room] 计划自动提问触发', { source, delay, questionFromURL });
      autoQuestionTimerRef.current = window.setTimeout(() => {
        triggerAutoQuestion();
      }, delay);
    },
    [questionFromURL, hasSent, triggerAutoQuestion]
  );

  // 事件触发源1：最后一条为AI定稿段落，且当前AI静音一段时间（去抖）
  useEffect(() => {
    if (!questionFromURL || hasSent || autoQuestionScheduledRef.current) return;

    const lastMsg = msgHistory[msgHistory.length - 1];
    const lastIsAIFinalParagraph =
      !!lastMsg &&
      lastMsg.user === botName &&
      lastMsg.paragraph &&
      !lastMsg.isInterrupted &&
      typeof lastMsg.value === 'string' &&
      lastMsg.value.length > 0;

    if (lastIsAIFinalParagraph && !isAITalking) {
      if (aiIdleDebounceTimerRef.current) {
        clearTimeout(aiIdleDebounceTimerRef.current);
        aiIdleDebounceTimerRef.current = null;
      }
      // 等待AI静默稳定300ms，再延迟400ms触发
      aiIdleDebounceTimerRef.current = window.setTimeout(() => {
        scheduleAutoQuestionOnce('aiFinalParagraphIdle', 400);
      }, 300);
    }

    // 兼容原逻辑：仅有欢迎语（单条AI消息）且AI不再说话
    const isWelcomeOnly =
      msgHistory.length === 1 && msgHistory[0]?.user === botName && !isAITalking;
    if (isWelcomeOnly) {
      scheduleAutoQuestionOnce('welcomeFinished', 500);
    }

    return () => {
      if (aiIdleDebounceTimerRef.current) {
        clearTimeout(aiIdleDebounceTimerRef.current);
        aiIdleDebounceTimerRef.current = null;
      }
    };
  }, [msgHistory, isAITalking, questionFromURL, hasSent, botName, scheduleAutoQuestionOnce]);

  // 事件触发源2（兜底）：进入房间固定时间后尝试触发（避免错过事件）
  useEffect(() => {
    if (!questionFromURL || hasSent || autoQuestionScheduledRef.current) return;

    fallbackAutoQuestionTimerRef.current = window.setTimeout(() => {
      if (!autoQuestionScheduledRef.current && !hasSent) {
        scheduleAutoQuestionOnce('fallbackTimer', 0);
      }
    }, 4000);

    return () => {
      if (fallbackAutoQuestionTimerRef.current) {
        clearTimeout(fallbackAutoQuestionTimerRef.current);
        fallbackAutoQuestionTimerRef.current = null;
      }
    };
  }, [questionFromURL, hasSent, scheduleAutoQuestionOnce]);

  // 卸载清理
  useEffect(() => {
    return () => {
      if (autoQuestionTimerRef.current) {
        clearTimeout(autoQuestionTimerRef.current);
        autoQuestionTimerRef.current = null;
      }
      if (aiIdleDebounceTimerRef.current) {
        clearTimeout(aiIdleDebounceTimerRef.current);
        aiIdleDebounceTimerRef.current = null;
      }
      if (fallbackAutoQuestionTimerRef.current) {
        clearTimeout(fallbackAutoQuestionTimerRef.current);
        fallbackAutoQuestionTimerRef.current = null;
      }
    };
  }, []);

  // ==================== 用户说话检测 ====================
  useUserTalkingDetection(
    msgHistory,
    localUser?.userId,
    {
      silenceTimeout: 3000,
      enableAutoDetection: true,
    },
    {
      onTalkingStateChange: useCallback(
        (isTalking: boolean) => {
          dispatch(updateUserTalking({ isUserTalking: isTalking }));
        },
        [dispatch]
      ),
      onUserStartTalking: useCallback(() => {
        logger.debug('[Room] 用户开始说话');
      }, []),
      onUserStopTalking: useCallback(() => {
        logger.debug('[Room] 用户停止说话');
      }, []),
    }
  );

  // ==================== 可配置超时退出逻辑 ====================
  useEffect(() => {
    let timer: NodeJS.Timeout;

    // 只有当设置了超时时间且既没有AI在说话，也没有用户在说话时，启动倒计时
    if (autoExitTimeout > 0 && !isAITalking && !isUserTalking) {
      timer = setTimeout(() => {
        logger.info(`[Room] ${autoExitTimeout / 1000}秒无活动，自动离开房间`);
        const leaveBtn = document.getElementById('hangup-button');
        if (leaveBtn) {
          logger.info('[Room] 点击退出按钮');
          leaveBtn.click();
        } else {
          logger.warn('[Room] 未找到退出按钮，尝试其他退出方式');
          // 如果没有找到按钮，可以尝试其他方式退出
          // 比如调用路由跳转或其他退出逻辑
        }
      }, autoExitTimeout);

      logger.debug(`[Room] 启动${autoExitTimeout / 1000}秒自动退出倒计时`);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
        logger.debug('[Room] 清除自动退出倒计时');
      }
    };
  }, [isAITalking, isUserTalking, autoExitTimeout]);

  // ==================== 麦克风静音控制逻辑 ====================

  // 统一管理麦克风静音状态的核心逻辑
  const shouldMuteMic = useMemo(() => {
    // 最高优先级：用户手动关闭麦克风
    if (!isAudioPublished) {
      return true;
    }

    // 第二优先级："按住说话"模式
    if (pushToTalkMode) {
      return !isHoldingTalkButton;
    }

    // 最低优先级：标准模式下的自动静音
    return isAITalking && !effectiveInterruptMode;
  }, [isAudioPublished, pushToTalkMode, isHoldingTalkButton, isAITalking, effectiveInterruptMode]);

  // ==================== RTC静音控制 ====================
  React.useEffect(() => {
    const isRTCReady =
      RtcClient.engine && typeof RtcClient.engine === 'object' && RtcClient.engine !== null;

    // 更严格的就绪检查：确保引擎存在且已加入房间
    const isFullyReady = isRTCReady && RtcClient.basicInfo?.room_id;

    if (!isFullyReady) {
      logger.debug('[Room] RTC连接未完全就绪，延迟静音操作');
      // 如果RTC未就绪，设置一个延迟后重试，给按住说话模式更多初始化时间
      const retryTimer = setTimeout(
        () => {
          if (RtcClient.engine && RtcClient.basicInfo?.room_id) {
            try {
              logger.debug('[Room] 延迟执行静音操作，当前状态:', { shouldMuteMic, pushToTalkMode });
              RtcClient.muteLocalAudio(shouldMuteMic);
            } catch (error) {
              logger.warn('[Room] 延迟静音操作仍然失败:', error);
            }
          }
        },
        pushToTalkMode ? 800 : 200
      ); // 按住说话模式需要更长的初始化时间

      return () => clearTimeout(retryTimer);
    }

    // 对于按住说话模式，增加额外的初始化保护
    if (pushToTalkMode) {
      logger.debug('[Room] 按住说话模式，延迟执行静音操作以避免RTC状态冲突');
      const pushToTalkTimer = setTimeout(() => {
        try {
          RtcClient.muteLocalAudio(shouldMuteMic);
        } catch (error) {
          logger.warn('[Room] 按住说话模式静音操作失败:', error);
        }
      }, 300);

      return () => clearTimeout(pushToTalkTimer);
    }

    try {
      RtcClient.muteLocalAudio(shouldMuteMic);
    } catch (error) {
      logger.warn('[Room] 静音/取消静音操作失败:', error);
    }
  }, [shouldMuteMic, pushToTalkMode]);

  // ==================== 事件处理器（使用useCallback优化） ====================



  // ==================== 计算属性（使用useMemo优化） ====================
  // 移除未使用的 inputBoxConfig




  // ==================== 渲染优化组件 ====================
  const renderInputBox = useMemo(
    () => (
      <BottomControls
        mode="room"
        pushToTalkMode={pushToTalkMode}
        isHoldingTalkButton={isHoldingTalkButton}
        setIsHoldingTalkButton={setIsHoldingTalkButton}
        isAITalking={isAITalking}
        onInterrupt={voiceInteraction.handleManualInterrupt}
        volume={audioSystem.currentVolume * 100}
        isVolumeVisible={audioSystem.isMonitoring}
        isMuted={shouldMuteMic}
      />
    ),
    [
      pushToTalkMode,
      isHoldingTalkButton,
      isAITalking,
      voiceInteraction.handleManualInterrupt,
      audioSystem.currentVolume,
      audioSystem.isMonitoring,
      shouldMuteMic,
    ]
  );

  const renderSettingsPanel = useMemo(
    () => (
      <SettingsPanel
        items={settingsConfig}
        className={styles.settingsBtnWrapper}
        buttonClassName={styles.settingsBtn}
        position="br"
      />
    ),
    [settingsConfig]
  );

  // ==================== 主渲染 ====================
  // 使用统一的布局检测 hook
  const { chatLayoutMode } = useLayout();

  return (
    <ChatLayout
      mode={chatLayoutMode}
      avatar={
        <AvatarContainer
          mode="chat"
          showUserTag={false}
        />
      }
      inputBox={renderInputBox}
      bubbles={<Conversation />}
    >
      <>
        {/* 统一集成至 BottomControls：移除原音量指示和打断按钮 */}
        {renderSettingsPanel}
      </>
    </ChatLayout>
  );
}

export default React.memo(Room);
